/**
 * SPEC.md §4 — the diagram-type registry: the single place that knows about
 * all 27 Mermaid diagram types Plotter supports (Appendix A). Every other
 * M4 module (the New menu, the status bar, the error card's docs link) is a
 * thin consumer of `DIAGRAM_TYPES` and `detectDiagramType` — this file owns
 * the catalog data and the one piece of classification logic; nothing here
 * renders or touches the DOM.
 *
 * Templates are copied verbatim from Appendix A, not paraphrased — SPEC.md
 * calls them "known-good" and the template smoke test (diagramTypes.smoke)
 * is what proves that stays true across Mermaid upgrades.
 */

export type Category = 'process' | 'interaction' | 'structure' | 'planning' | 'data';

export interface DiagramType {
  id: string;
  label: string;
  /** The opening token as it appears in this catalog's template, for detection. */
  keyword: string;
  /** Other opening tokens Mermaid accepts for the same type (old or future forms). */
  aliases?: string[];
  category: Category;
  status: 'stable' | 'beta' | 'plugin';
  docsUrl: string;
  /** A valid, non-trivial starter diagram — SPEC.md Appendix A, copied verbatim. */
  template: string;
}

export const DIAGRAM_TYPES: DiagramType[] = [
  {
    id: 'flowchart',
    label: 'Flowchart',
    keyword: 'flowchart',
    aliases: ['graph'],
    category: 'process',
    status: 'stable',
    docsUrl: 'https://mermaid.js.org/syntax/flowchart.html',
    template: `flowchart TD
  A([Alert fires]) --> B{Known signature?}
  B -- yes --> C[Match runbook]
  B -- no --> D[Page on-call]
  C --> E{Safe to auto-remediate?}
  E -- yes --> F[Run remediation]
  E -- no --> D
  F --> G{Verified healthy?}
  G -- yes --> H([Resolve and log])
  G -- no --> D
  D --> I[Human investigates] --> H
`,
  },
  {
    id: 'sequence',
    label: 'Sequence',
    keyword: 'sequenceDiagram',
    category: 'interaction',
    status: 'stable',
    docsUrl: 'https://mermaid.js.org/syntax/sequenceDiagram.html',
    template: `sequenceDiagram
  autonumber
  participant C as Client
  participant G as Gateway
  participant S as Service
  participant D as Database

  C->>G: POST /orders
  G->>S: createOrder(payload)
  S->>D: INSERT order
  D-->>S: order id
  alt Payment authorised
    S-->>G: 201 Created
  else Declined
    S-->>G: 402 Payment Required
  end
  G-->>C: response
  Note over S,D: Wrapped in a transaction
`,
  },
  {
    id: 'class',
    label: 'Class',
    keyword: 'classDiagram',
    category: 'structure',
    status: 'stable',
    docsUrl: 'https://mermaid.js.org/syntax/classDiagram.html',
    template: `classDiagram
  class Resolver {
    +String name
    +run(ctx) Result
    +verify(ctx) bool
  }
  class Runbook {
    +String id
    +int timeoutSeconds
    +steps() Step[]
  }
  class Step {
    +String command
    +execute() Output
  }
  <<interface>> Resolver
  Resolver --> Runbook : selects
  Runbook "1" o-- "many" Step : contains
`,
  },
  {
    id: 'state',
    label: 'State',
    keyword: 'stateDiagram-v2',
    aliases: ['stateDiagram'],
    category: 'structure',
    status: 'stable',
    docsUrl: 'https://mermaid.js.org/syntax/stateDiagram.html',
    template: `stateDiagram-v2
  [*] --> Pending
  Pending --> Building : manifest accepted
  Building --> Failed : build error
  Building --> Deploying
  Deploying --> Healthy : probes pass
  Deploying --> RollingBack : probes fail
  RollingBack --> Healthy
  Failed --> [*]
  Healthy --> [*]

  state Deploying {
    [*] --> Pulling
    Pulling --> Starting
    Starting --> [*]
  }
`,
  },
  {
    id: 'er',
    label: 'Entity relationship',
    keyword: 'erDiagram',
    category: 'structure',
    status: 'stable',
    docsUrl: 'https://mermaid.js.org/syntax/entityRelationshipDiagram.html',
    template: `erDiagram
  INCIDENT ||--o{ ALERT : groups
  INCIDENT ||--o{ COMMAND : triggers
  RUNBOOK ||--o{ COMMAND : defines
  INCIDENT {
    string number PK
    string state
    datetime opened_at
  }
  ALERT {
    string source
    string source_alert_id
  }
  RUNBOOK {
    string name
    int timeout_seconds
  }
`,
  },
  {
    id: 'journey',
    label: 'User journey',
    keyword: 'journey',
    category: 'process',
    status: 'stable',
    docsUrl: 'https://mermaid.js.org/syntax/userJourney.html',
    template: `journey
  title Reporting a production issue
  section Detect
    Notice the alert: 3: On-call
    Open the dashboard: 4: On-call
  section Diagnose
    Read the runbook: 5: On-call
    Check recent deploys: 2: On-call, Dev
  section Resolve
    Roll back: 4: Dev
    Write the postmortem: 3: On-call, Dev
`,
  },
  {
    id: 'gantt',
    label: 'Gantt',
    keyword: 'gantt',
    category: 'planning',
    status: 'stable',
    docsUrl: 'https://mermaid.js.org/syntax/gantt.html',
    template: `gantt
  title Migration plan
  dateFormat YYYY-MM-DD
  axisFormat %b %d

  section Prepare
  Inventory services   :done,   a1, 2026-06-01, 10d
  Dry run in staging   :active, a2, after a1, 8d

  section Cut over
  Move read traffic    :        b1, after a2, 5d
  Move write traffic   :crit,   b2, after b1, 3d

  section Clean up
  Retire old stack     :        c1, after b2, 6d
`,
  },
  {
    id: 'pie',
    label: 'Pie',
    keyword: 'pie',
    category: 'data',
    status: 'stable',
    docsUrl: 'https://mermaid.js.org/syntax/pie.html',
    template: `pie showData
  title Incidents by cause, last quarter
  "Config change" : 42
  "Capacity" : 27
  "Upstream dependency" : 18
  "Code defect" : 13
`,
  },
  {
    id: 'quadrant',
    label: 'Quadrant',
    keyword: 'quadrantChart',
    category: 'data',
    status: 'stable',
    docsUrl: 'https://mermaid.js.org/syntax/quadrantChart.html',
    template: `quadrantChart
  title Effort against impact
  x-axis Low effort --> High effort
  y-axis Low impact --> High impact
  quadrant-1 Do now
  quadrant-2 Plan it
  quadrant-3 Drop it
  quadrant-4 Quick wins
  Alert dedup: [0.3, 0.8]
  Log retention: [0.7, 0.3]
  Runbook rewrite: [0.8, 0.85]
  Dashboard tidy: [0.2, 0.25]
`,
  },
  {
    id: 'requirement',
    label: 'Requirement',
    keyword: 'requirementDiagram',
    category: 'structure',
    status: 'stable',
    docsUrl: 'https://mermaid.js.org/syntax/requirementDiagram.html',
    template: `requirementDiagram
  requirement availability {
    id: 1
    text: Service stays available at 99.9% monthly
    risk: high
    verifymethod: test
  }

  functionalRequirement failover {
    id: 1.1
    text: Traffic shifts to the standby region within 60s
    risk: high
    verifymethod: demonstration
  }

  element healthcheck {
    type: probe
  }

  availability - contains -> failover
  healthcheck - verifies -> failover
`,
  },
  {
    id: 'gitGraph',
    label: 'Git graph',
    keyword: 'gitGraph',
    category: 'planning',
    status: 'stable',
    docsUrl: 'https://mermaid.js.org/syntax/gitgraph.html',
    template: `gitGraph
  commit id: "init"
  branch develop
  commit id: "add parser"
  commit id: "add renderer"
  checkout main
  merge develop tag: "v0.1.0"
  branch hotfix
  commit id: "fix escaping"
  checkout main
  merge hotfix tag: "v0.1.1"
`,
  },
  {
    id: 'c4Context',
    label: 'C4 context',
    keyword: 'C4Context',
    category: 'structure',
    status: 'beta',
    docsUrl: 'https://mermaid.js.org/syntax/c4.html',
    template: `C4Context
  title System context for the incident platform

  Person(sre, "SRE", "Responds to production alerts")
  System(platform, "Incident platform", "Ingests alerts and drives remediation")
  System_Ext(monitor, "Monitoring", "Emits alerts")
  System_Ext(chat, "Chat", "Receives notifications")

  Rel(monitor, platform, "Sends alerts", "HTTPS")
  Rel(platform, chat, "Posts updates", "Webhook")
  Rel(sre, platform, "Reviews and acts")
`,
  },
  {
    id: 'c4Container',
    label: 'C4 container',
    keyword: 'C4Container',
    category: 'structure',
    status: 'beta',
    docsUrl: 'https://mermaid.js.org/syntax/c4.html',
    template: `C4Container
  title Containers inside the incident platform

  Person(sre, "SRE")
  Container_Boundary(c, "Incident platform") {
    Container(api, "Ingest API", "Node", "Accepts alert webhooks")
    Container(worker, "Resolver", "Node", "Runs runbooks")
    ContainerDb(db, "Store", "PostgreSQL", "Incidents and runbooks")
  }

  Rel(sre, api, "Queries", "HTTPS")
  Rel(api, db, "Reads and writes")
  Rel(worker, db, "Reads and writes")
`,
  },
  {
    id: 'c4Component',
    label: 'C4 component',
    keyword: 'C4Component',
    category: 'structure',
    status: 'beta',
    docsUrl: 'https://mermaid.js.org/syntax/c4.html',
    template: `C4Component
  title Components inside the resolver

  Container_Boundary(w, "Resolver") {
    Component(sel, "Runbook selector", "Module", "Matches alert to runbook")
    Component(exec, "Step executor", "Module", "Runs commands")
    Component(ver, "Verifier", "Module", "Confirms the fix held")
  }

  Rel(sel, exec, "Hands off steps")
  Rel(exec, ver, "Triggers verification")
`,
  },
  {
    id: 'c4Dynamic',
    label: 'C4 dynamic',
    keyword: 'C4Dynamic',
    category: 'structure',
    status: 'beta',
    docsUrl: 'https://mermaid.js.org/syntax/c4.html',
    template: `C4Dynamic
  title Remediation sequence

  Container(api, "Ingest API")
  Container(worker, "Resolver")
  ContainerDb(db, "Store")

  Rel(api, db, "1. Writes the incident")
  Rel(api, worker, "2. Enqueues the job")
  Rel(worker, db, "3. Records the outcome")
`,
  },
  {
    id: 'c4Deployment',
    label: 'C4 deployment',
    keyword: 'C4Deployment',
    category: 'structure',
    status: 'beta',
    docsUrl: 'https://mermaid.js.org/syntax/c4.html',
    template: `C4Deployment
  title Deployment topology

  Deployment_Node(cloud, "Cloud region", "eu-west-1") {
    Deployment_Node(k8s, "Kubernetes cluster", "AKS") {
      Container(api, "Ingest API", "Node")
      Container(worker, "Resolver", "Node")
    }
    Deployment_Node(managed, "Managed data", "PaaS") {
      ContainerDb(db, "Store", "PostgreSQL")
    }
  }

  Rel(api, db, "TLS 5432")
  Rel(worker, db, "TLS 5432")
`,
  },
  {
    id: 'mindmap',
    label: 'Mindmap',
    keyword: 'mindmap',
    category: 'structure',
    status: 'stable',
    docsUrl: 'https://mermaid.js.org/syntax/mindmap.html',
    template: `mindmap
  root((Reliability))
    Detection
      Alerting rules
      Synthetic probes
    Response
      Runbooks
      On-call rotation
      Escalation policy
    Prevention
      Load testing
      Chaos drills
    Learning
      Postmortems
      Error budgets
`,
  },
  {
    id: 'timeline',
    label: 'Timeline',
    keyword: 'timeline',
    category: 'planning',
    status: 'stable',
    docsUrl: 'https://mermaid.js.org/syntax/timeline.html',
    template: `timeline
  title Platform milestones
  section 2024
    Q1 : First internal deploy
    Q3 : Alert ingestion live
  section 2025
    Q1 : Auto-remediation pilot
    Q4 : Multi-region failover
  section 2026
    Q2 : Self-service runbooks
`,
  },
  {
    id: 'sankey',
    label: 'Sankey',
    keyword: 'sankey-beta',
    aliases: ['sankey'],
    category: 'data',
    status: 'beta',
    docsUrl: 'https://mermaid.js.org/syntax/sankey.html',
    template: `sankey-beta

Alerts,Auto-resolved,540
Alerts,Escalated,180
Alerts,Suppressed,95
Escalated,Fixed by on-call,150
Escalated,Handed to engineering,30
`,
  },
  {
    id: 'xychart',
    label: 'XY chart',
    keyword: 'xychart-beta',
    aliases: ['xychart'],
    category: 'data',
    status: 'beta',
    docsUrl: 'https://mermaid.js.org/syntax/xyChart.html',
    template: `xychart-beta
  title "Mean time to resolve, minutes"
  x-axis [Jan, Feb, Mar, Apr, May, Jun]
  y-axis "Minutes" 0 --> 90
  bar [82, 74, 66, 51, 44, 38]
  line [82, 74, 66, 51, 44, 38]
`,
  },
  {
    id: 'block',
    label: 'Block',
    keyword: 'block-beta',
    aliases: ['block'],
    category: 'structure',
    status: 'beta',
    docsUrl: 'https://mermaid.js.org/syntax/block.html',
    template: `block-beta
  columns 3
  ingest["Ingest"]:3
  space:3
  norm["Normalizer"] router["Router"] store[("Store")]
  space:3
  resolver["Resolver"]:2 notify["Notifier"]

  ingest --> norm
  norm --> router
  router --> resolver
  router --> notify
  resolver --> store
`,
  },
  {
    id: 'packet',
    label: 'Packet',
    keyword: 'packet-beta',
    aliases: ['packet'],
    category: 'data',
    status: 'beta',
    docsUrl: 'https://mermaid.js.org/syntax/packet.html',
    template: `packet-beta
  0-15: "Source port"
  16-31: "Destination port"
  32-63: "Sequence number"
  64-95: "Acknowledgment number"
  96-99: "Data offset"
  100-105: "Reserved"
  106: "URG"
  107: "ACK"
  108: "PSH"
  109: "RST"
  110: "SYN"
  111: "FIN"
  112-127: "Window"
`,
  },
  {
    id: 'kanban',
    label: 'Kanban',
    keyword: 'kanban',
    category: 'process',
    status: 'stable',
    docsUrl: 'https://mermaid.js.org/syntax/kanban.html',
    template: `kanban
  Backlog
    task1[Rewrite the escalation runbook]
    task2[Add synthetic probe for checkout]
  In progress
    task3[Fix alert dedup window]@{ assigned: 'dineth' }
  Review
    task4[Region failover drill notes]
  Done
    task5[Upgrade the ingest pipeline]
`,
  },
  {
    id: 'architecture',
    label: 'Architecture',
    keyword: 'architecture-beta',
    aliases: ['architecture'],
    category: 'structure',
    status: 'beta',
    docsUrl: 'https://mermaid.js.org/syntax/architecture.html',
    template: `architecture-beta
  group api(cloud)[Cloud]

  service gateway(internet)[Gateway] in api
  service worker(server)[Resolver] in api
  service db(database)[Store] in api
  service bucket(disk)[Artifacts] in api

  gateway:R --> L:worker
  worker:R --> L:db
  worker:B --> T:bucket
`,
  },
  {
    id: 'radar',
    label: 'Radar',
    keyword: 'radar-beta',
    aliases: ['radar'],
    category: 'data',
    status: 'beta',
    docsUrl: 'https://mermaid.js.org/syntax/radar.html',
    template: `radar-beta
  title Service scorecard
  axis obs["Observability"], docs["Docs"], test["Test coverage"]
  axis perf["Performance"], sec["Security"], ops["Operability"]

  curve current["Today"]{70, 45, 60, 80, 55, 65}
  curve target["Target"]{90, 80, 85, 85, 90, 85}

  max 100
  min 0
`,
  },
  {
    id: 'treemap',
    label: 'Treemap',
    keyword: 'treemap-beta',
    aliases: ['treemap'],
    category: 'data',
    status: 'beta',
    docsUrl: 'https://mermaid.js.org/syntax/treemap.html',
    template: `treemap-beta
  "Cloud spend"
    "Compute"
      "Kubernetes": 4200
      "Batch jobs": 900
    "Storage"
      "Object store": 1400
      "Snapshots": 350
    "Network"
      "Egress": 1100
      "Load balancers": 300
`,
  },
  {
    id: 'zenuml',
    label: 'ZenUML',
    keyword: 'zenuml',
    category: 'interaction',
    status: 'plugin',
    docsUrl: 'https://mermaid.js.org/syntax/zenuml.html',
    template: `zenuml
  title Order placement
  @Actor Client
  @Boundary Gateway
  @Control Service
  @Database Store

  Client->Gateway.placeOrder() {
    Gateway->Service.create() {
      Service->Store.insert()
      return orderId
    }
    return 201
  }
`,
  },
];

function isBlank(line: string | undefined): boolean {
  return (line ?? '').trim() === '';
}

function isComment(line: string | undefined): boolean {
  return (line ?? '').trim().startsWith('%%');
}

function isFrontmatterFence(line: string | undefined): boolean {
  return (line ?? '').trim() === '---';
}

/**
 * SPEC.md §4 "Type detection" — the leading token detection is built on.
 * Skips leading blank lines, one leading frontmatter block (`---`…`---`,
 * skipped past, never stripped), and `%%` comment lines, then returns the
 * first remaining line's leading token. Exported (not just an internal
 * step of `detectDiagramType`) because the error card's alias-mismatch
 * hint (issue #17) needs to know exactly which token the user wrote —
 * `detectDiagramType` alone only reveals which registry entry matched it.
 */
export function extractOpeningToken(source: string): string | null {
  const lines = source.split('\n');
  let i = 0;

  while (i < lines.length && isBlank(lines[i])) i++;

  if (isFrontmatterFence(lines[i])) {
    i++;
    while (i < lines.length && !isFrontmatterFence(lines[i])) i++;
    if (i < lines.length) i++;
  }

  while (i < lines.length && (isBlank(lines[i]) || isComment(lines[i]))) i++;

  const line = lines[i];
  if (line === undefined) return null;

  return line.trim().split(/\s+/)[0] ?? null;
}

/**
 * Display-only — never mutates or rewrites `source`. Matches
 * `extractOpeningToken`'s result against every entry's `keyword` and
 * `aliases`.
 */
export function detectDiagramType(source: string): DiagramType | null {
  const token = extractOpeningToken(source);
  if (token === null) return null;
  return DIAGRAM_TYPES.find((type) => type.keyword === token || type.aliases?.includes(token)) ?? null;
}
